import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { roleAtLeast } from '@os/shared';
import { Button } from '../../ui';
import type { SyncProvider } from '../../lib/sync/provider';

const lowered = (p: SyncProvider) => p.status === 'revoked' || (p.role !== null && !roleAtLeast(p.role, 'editor'));

/**
 * Sync alerts in the board bar (spec §7.5): access removed, and offline edits refused (revoked or role lowered) with
 * a JSON export. The everyday state (saved, saving, offline, read-only) is the save indicator's (P16-3).
 */
/** `onExport` absent (no `can_export`): the refused-edits banner offers no JSON export. */
export function SyncStatus({ provider, onExport }: { provider: SyncProvider; onExport?: () => void }) {
  const { t } = useTranslation();
  useSyncExternalStore(provider.subscribe, () => `${provider.status}|${provider.role}|${provider.pendingCount}`);
  // Sticky: the next full sync resets the pending count, but the refused edits are still only on this device.
  const [rejected, setRejected] = useState(false);
  useEffect(() => {
    const check = () => {
      if (lowered(provider) && provider.pendingCount > 0) setRejected(true);
    };
    check();
    return provider.subscribe(check);
  }, [provider]);

  let body: ReactNode = null;
  if (rejected) {
    body = (
      <div role="alert" className="flex flex-wrap items-center gap-2 font-sans text-sm text-fg">
        <span>{t('sync.rejected')}</span>
        {onExport && (
          <Button size="sm" onClick={onExport}>
            {t('board.exportJson')}
          </Button>
        )}
      </div>
    );
  } else if (provider.status === 'revoked') {
    body = (
      <span role="alert" className="font-sans text-sm text-fg">
        {t('sync.revoked')}
      </span>
    );
  }
  // Offline, read-only and saved now live in the title pill's save indicator (P16-3): only the alerts stay here.
  // `data-sync-status` exposes the connection state whatever is displayed (read-only boards hide "Synced").
  return (
    <div data-sync-status={provider.status} className="contents">
      {body}
    </div>
  );
}
